/**
 * PermissionCodesPanel — uprawnienia nadawane KODEM, nie drzewem (F24 Kawałek 1).
 *
 * Kartoteki mają hierarchię (kartoteka → klasa → typ), więc nadaje się je drzewem. Reszta
 * katalogu — procesy, pulpit, słowniki, ustawienia — hierarchii nie ma: albo się może, albo nie.
 * Dlatego tu jest płaska lista pogrupowana po obszarze, z tym samym trójstanem co w drzewie.
 *
 * Reguła z tej listy wisi zawsze na poziomie CAŁEJ FIRMY — to świadome ograniczenie modelu:
 * „edycja słownika w trzeciej gałęzi" nie jest pojęciem, które ktokolwiek chciałby ustawiać.
 *
 * Lista jest dwupoziomowa: nagłówek grupy (obszar albo moduł) ma WŁASNĄ kontrolkę, która ustawia
 * wszystkie uprawnienia pod nim, a rozwinięcie pokazuje je pojedynczo. Przy module z trzema
 * prawami klikanie po jednym jest znośne, przy czterdziestu — nie.
 */

import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';

import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import type { CatalogDefinition } from '@/components/permissions/permission-areas';
import {
  type PermissionCellChoice,
  type PermissionCellValue,
  PermissionSelect,
} from '@/components/permissions/PermissionSelect';

import {
  PERMISSION_TABLE_CARD_CLASS,
  PERMISSION_TABLE_CLASS,
  PERMISSION_THEAD_CLASS,
} from './permission-table-classes';

interface PermissionCodesPanelProps {
  definitions: CatalogDefinition[];
  /** Po czym grupować — domyślnie kategoria katalogu, na zakładce modułów: slug modułu. */
  groupKeyOf?: (def: CatalogDefinition) => string;
  /** Nazwa grupy do pokazania. Brak = etykieta z tłumaczeń kategorii, a w ostateczności klucz. */
  groupLabelOf?: (key: string) => string | null;
  /** Stan komórki: jawna reguła kodowa podmiotu albo „bez własnej reguły". */
  valueOf: (code: string) => PermissionCellChoice;
  /** Co wyjdzie przy braku własnej reguły — znacznik obok kontrolki. */
  inheritedOf: (code: string) => boolean;
  /** Skąd płynie wynik (rola / zespół) — tylko przy podmiocie OSOBA. */
  sourceOf?: (code: string) => string | null;
  onExplain?: (code: string) => void;
  onChange: (code: string, next: PermissionCellChoice) => void;
  /** Ustawienie CAŁEJ grupy jednym ruchem. */
  onChangeGroup: (codes: string[], next: PermissionCellChoice) => void;
  inheritLabel: string;
  editing: boolean;
  emptyHint: string;
}

export function PermissionCodesPanel({
  definitions,
  groupKeyOf,
  groupLabelOf,
  valueOf,
  inheritedOf,
  sourceOf,
  onExplain,
  onChange,
  onChangeGroup,
  inheritLabel,
  editing,
  emptyHint,
}: PermissionCodesPanelProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  /** Grupujemy po kategorii katalogu — to jedyny podział, który niesie sam katalog. */
  const groups = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const map = new Map<string, CatalogDefinition[]>();
    for (const def of definitions) {
      const label = (def.name ?? def.code).toLowerCase();
      if (needle !== '' && !label.includes(needle) && !def.code.toLowerCase().includes(needle)) {
        continue;
      }
      const key = groupKeyOf?.(def) ?? def.category ?? def.moduleCode;
      const list = map.get(key) ?? [];
      list.push(def);
      map.set(key, list);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], 'pl'));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- groupKeyOf jest stabilne per zakładka
  }, [definitions, filter]);

  /**
   * Stan nagłówka grupy: jednomyślny stan wszystkich uprawnień pod nim albo PUSTO, gdy się
   * różnią. Ta sama zasada, co w drzewie kartotek — pusto znaczy „zajrzyj niżej".
   */
  function groupValue(defs: CatalogDefinition[]): PermissionCellValue {
    const values = defs.map((d) => valueOf(d.code));
    const first = values[0];
    return first !== undefined && values.every((v) => v === first) ? first : 'mixed';
  }

  if (definitions.length === 0) {
    return <p className="p-6 text-sm text-text-muted">{emptyHint}</p>;
  }

  function toggle(key: string): void {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="overflow-x-auto">
      <div className="border-b border-border-primary px-3 py-2">
        <Input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('tenant.permissions.center.codes.filterPlaceholder')}
          aria-label={t('tenant.permissions.center.codes.filterPlaceholder')}
          prefixIcon={<Search className="h-3.5 w-3.5" />}
          className="max-w-sm"
        />
      </div>

      {groups.length === 0 ? (
        <p className="p-6 text-sm text-text-muted">
          {t('tenant.permissions.center.codes.noMatches')}
        </p>
      ) : (
        <div className={PERMISSION_TABLE_CARD_CLASS}>
          <table className={PERMISSION_TABLE_CLASS} data-skin="antd">
            <thead className={PERMISSION_THEAD_CLASS}>
              <tr className="text-left text-text-secondary">
                <th className="px-3 py-2 font-medium">
                  {t('tenant.permissions.center.codes.colPermission')}
                </th>
                <th className="w-40 px-3 py-2 font-medium">
                  {t('tenant.permissions.center.codes.colState')}
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.map(([category, defs]) => (
                <Fragment key={category}>
                  <tr className="border-b border-border-primary bg-bg-tertiary/40">
                    <td className="px-3 py-1">
                      <div className="flex items-center gap-1">
                        <IconButton
                          size="sm"
                          onClick={() => toggle(category)}
                          aria-label={t('tenant.permissions.center.toggle')}
                        >
                          {collapsed.has(category) ? (
                            <ChevronRight size={14} aria-hidden="true" />
                          ) : (
                            <ChevronDown size={14} aria-hidden="true" />
                          )}
                        </IconButton>
                        <span className="font-medium text-text-primary">
                          {groupLabelOf?.(category) ??
                            t(`tenant.permissions.center.codes.category.${category}`, {
                              defaultValue: category,
                            })}
                        </span>
                        <span className="text-xs text-text-muted">({defs.length})</span>
                      </div>
                    </td>
                    <td className="px-3 py-1">
                      <PermissionSelect
                        value={groupValue(defs)}
                        // Nagłówek grupy nie ma własnego „skąd" — to zbiór, nie jedno uprawnienie.
                        inheritedResult={null}
                        inheritLabel={inheritLabel}
                        disabled={!editing}
                        onChange={(next) =>
                          onChangeGroup(
                            defs.map((d) => d.code),
                            next,
                          )
                        }
                      />
                    </td>
                  </tr>
                  {!collapsed.has(category) &&
                    defs.map((def) => (
                      <tr
                        key={def.code}
                        className="border-b border-border-primary/25 even:bg-bg-tertiary/20"
                      >
                        <td className="px-3 py-1">
                          <div className="pl-7">
                            <p className="text-text-primary">{def.name ?? def.code}</p>
                            {/* Kod zostaje na wierzchu: to po nim szuka się w dzienniku zmian
                              i o nim mówi komunikat „brak uprawnień". */}
                            <p className="text-xs text-text-muted">{def.code}</p>
                          </div>
                        </td>
                        <td className="px-3 py-1">
                          <PermissionSelect
                            value={valueOf(def.code)}
                            inheritedResult={inheritedOf(def.code)}
                            inheritedFrom={sourceOf?.(def.code) ?? null}
                            inheritLabel={inheritLabel}
                            onExplain={onExplain ? () => onExplain(def.code) : undefined}
                            disabled={!editing}
                            onChange={(next) => onChange(def.code, next)}
                          />
                        </td>
                      </tr>
                    ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
